import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, CalendarIcon, Save, UserPlus, Check, Trash2, ClipboardCheck, History } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const typeBadge = (t) => {
  const m = { regular: 'border-blue-200 text-blue-700', visitor: 'border-amber-200 text-amber-700', occasional: 'border-teal-200 text-teal-700', inactive: 'border-slate-300 text-slate-500' };
  return m[t] || 'border-slate-200 text-slate-600';
};

export default function Attendance() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [people, setPeople] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('regular');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const [pastDates, setPastDates] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/events'), api.get('/people'), api.get('/attendance/dates')]).then(([e, p, d]) => {
      setEvents(e.data);
      setPeople(p.data);
      setPastDates(d.data);
      const def = e.data.find(ev => ev.is_default);
      if (def) setSelectedEvent(def.event_id);
      const today = new Date();
      const daysSinceSun = (today.getDay() + 7) % 7;
      const lastSun = new Date(today);
      lastSun.setDate(today.getDate() - (daysSinceSun === 0 ? 0 : daysSinceSun));
      setSelectedDate(lastSun);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedEvent || !selectedDate) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    api.get(`/attendance?event_id=${selectedEvent}&date=${dateStr}`).then(res => {
      const map = {};
      res.data.forEach(r => { map[r.person_id] = r.present; });
      setAttendance(map);
    }).catch(console.error);
  }, [selectedEvent, selectedDate]);

  const toggleAttendance = (personId) => {
    setAttendance(prev => ({ ...prev, [personId]: !prev[personId] }));
  };

  const markAllPresent = () => {
    const map = {};
    filtered.forEach(p => { map[p.person_id] = true; });
    setAttendance(prev => ({ ...prev, ...map }));
  };

  const clearAll = () => {
    const map = {};
    people.forEach(p => { map[p.person_id] = false; });
    setAttendance(map);
  };

  const saveAttendance = async () => {
    if (!selectedEvent || !selectedDate) return;
    setSaving(true);
    try {
      const records = people.map(p => ({ person_id: p.person_id, present: !!attendance[p.person_id] }));
      await api.post('/attendance', { event_id: selectedEvent, date: format(selectedDate, 'yyyy-MM-dd'), records });
      toast.success('Attendance saved successfully');
      // Refresh past dates
      api.get('/attendance/dates').then(res => setPastDates(res.data));
    } catch { toast.error('Failed to save attendance'); }
    finally { setSaving(false); }
  };

  const addQuickPerson = async () => {
    if (!newFirst.trim() || !newLast.trim()) return;
    try {
      const res = await api.post('/people', { first_name: newFirst.trim(), last_name: newLast.trim(), person_type: 'visitor' });
      setPeople(prev => [...prev, res.data]);
      setAttendance(prev => ({ ...prev, [res.data.person_id]: true }));
      setAddOpen(false);
      setNewFirst('');
      setNewLast('');
      toast.success('Person added and marked present');
    } catch { toast.error('Failed to add person'); }
  };

  const deletePastDate = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/attendance/date?event_id=${deleteTarget.event_id}&date=${deleteTarget.date}`);
      setPastDates(prev => prev.filter(d => !(d.date === deleteTarget.date && d.event_id === deleteTarget.event_id)));
      setDeleteTarget(null);
      toast.success('Attendance records deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = useMemo(() =>
    people
      .filter(p => {
        if (search) {
          const q = search.toLowerCase();
          if (!p.first_name.toLowerCase().includes(q) && !p.last_name.toLowerCase().includes(q)) return false;
        }
        if (typeFilter !== 'all' && p.person_type !== typeFilter) return false;
        return true;
      })
      .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || '')),
    [people, search, typeFilter]
  );

  const presentCount = Object.values(attendance).filter(Boolean).length;

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <Tabs defaultValue="mark">
        <TabsList className="bg-slate-100 gap-1 p-1">
          <TabsTrigger value="mark" data-testid="attendance-tab-mark" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />Mark Attendance</TabsTrigger>
          <TabsTrigger value="history" data-testid="attendance-tab-history" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white"><History className="h-3.5 w-3.5 mr-1.5" />Past Records{pastDates.length > 0 && <Badge className="ml-1.5 bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px] h-5 px-1.5">{pastDates.length}</Badge>}</TabsTrigger>
        </TabsList>

        <TabsContent value="mark" className="mt-4 space-y-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end flex-wrap">
                <div className="w-full sm:w-56">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Service / Event</Label>
                  <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                    <SelectTrigger data-testid="event-select" className="border-slate-300"><SelectValue placeholder="Select event" /></SelectTrigger>
                    <SelectContent>
                      {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-auto">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Date</Label>
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <Button data-testid="date-picker-trigger" variant="outline" className="w-full sm:w-52 justify-start text-left font-normal border-slate-300">
                        <CalendarIcon className="h-4 w-4 mr-2 text-slate-500" />
                        {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={selectedDate} onSelect={(d) => { setSelectedDate(d); setCalOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-2 ml-auto flex-wrap">
                  {selectedDate && format(selectedDate, 'yyyy-MM-dd') !== format(new Date(), 'yyyy-MM-dd') && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 self-center">Editing past date</Badge>
                  )}
                  <Button data-testid="mark-all-present" variant="outline" size="sm" onClick={markAllPresent} className="border-slate-300">
                    <Check className="h-4 w-4 mr-1" />Mark All
                  </Button>
                  <Button data-testid="clear-all" variant="outline" size="sm" onClick={clearAll} className="border-slate-300 text-slate-500">
                    Clear All
                  </Button>
                  <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="quick-add-person" variant="outline" size="sm" className="border-slate-300"><UserPlus className="h-4 w-4 mr-1" />Add Person</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-['Outfit']">Quick Add Person</DialogTitle></DialogHeader>
                      <div className="space-y-3 pt-2">
                        <div><Label>First Name</Label><Input data-testid="quick-add-first" value={newFirst} onChange={e => setNewFirst(e.target.value)} className="mt-1" /></div>
                        <div><Label>Last Name</Label><Input data-testid="quick-add-last" value={newLast} onChange={e => setNewLast(e.target.value)} className="mt-1" /></div>
                        <Button data-testid="quick-add-submit" onClick={addQuickPerson} className="w-full bg-blue-700 hover:bg-blue-800">Add & Mark Present</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Outfit']">Attendance</CardTitle>
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{presentCount} / {people.length} present</Badge>
              </div>
              <div className="flex gap-3 mt-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="attendance-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people..." className="pl-10 border-slate-300" />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="attendance-type-filter" className="w-40 border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="occasional">Occasional</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                {filtered.map(p => {
                  const photoUrl = p.photo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.photo_path}` : null;
                  return (
                  <label
                    key={p.person_id}
                    data-testid={`attendance-row-${p.person_id}`}
                    className="flex items-center gap-4 py-3 px-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <Checkbox
                      data-testid={`attendance-check-${p.person_id}`}
                      checked={!!attendance[p.person_id]}
                      onCheckedChange={() => toggleAttendance(p.person_id)}
                      className="border-slate-300 data-[state=checked]:bg-blue-700 data-[state=checked]:border-blue-700"
                    />
                    {photoUrl ? (
                      <img src={photoUrl} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600 shrink-0">
                        {p.first_name[0]}{(p.last_name || '')[0] || ''}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                      <span className="text-sm font-medium text-slate-900">{p.last_name || '—'}</span>
                      <span className="text-sm text-slate-600">{p.first_name}</span>
                    </div>
                    <Badge variant="outline" className={`text-xs shrink-0 ${typeBadge(p.person_type)}`}>
                      {p.person_type}
                    </Badge>
                  </label>
                  );
                })}
                {filtered.length === 0 && <p className="text-center py-8 text-sm text-slate-500">No people found</p>}
              </div>
              <div className="pt-4 border-t border-slate-200 mt-4">
                <Button data-testid="save-attendance" onClick={saveAttendance} disabled={saving} className="w-full sm:w-auto bg-blue-700 hover:bg-blue-800">
                  <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Past Attendance Records</CardTitle>
            </CardHeader>
            <CardContent>
              {pastDates.length === 0 && <p className="text-sm text-slate-500 py-4">No past attendance records</p>}
              <div className="space-y-2" data-testid="past-attendance-list">
                {pastDates.map((d, i) => (
                  <div key={`${d.date}-${d.event_id}`} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-slate-500">{d.event_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{d.present} / {d.total} present</Badge>
                      <Button
                        data-testid={`edit-date-${d.date}`}
                        variant="outline"
                        size="sm"
                        className="text-xs border-slate-300"
                        onClick={() => {
                          setSelectedEvent(d.event_id);
                          setSelectedDate(new Date(d.date + 'T00:00:00'));
                          const tabTrigger = document.querySelector('[data-testid="attendance-tab-mark"]');
                          if (tabTrigger) tabTrigger.click();
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`delete-date-${d.date}`}
                        variant="outline"
                        size="sm"
                        className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => setDeleteTarget(d)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attendance Records?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all attendance records for <strong>{deleteTarget?.event_name}</strong> on <strong>{deleteTarget?.date && new Date(deleteTarget.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</strong> ({deleteTarget?.total} records).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-date" onClick={deletePastDate} className="bg-red-600 hover:bg-red-700">Delete Records</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
